// Singleton store for the navbar inbox unread count.
//
// Why a store: the badge component mounts twice (once per responsive navbar variant),
// each as its own React root. Without coordination each instance would open its own
// Supabase channel, do its own slug resolution, and its own initial fetch — doubling
// the realtime quota and PostgREST traffic for one UI element. The store hoists all of
// that out of React: subscribers register a callback, the store maintains a single
// channel and a single shared count, and all subscribers are notified together.

import qapi from 'views_shared/app/javascript/api';
import vlog from '../javascript/vlog';
import { fetchWithTokenRefresh, refreshAccessToken } from '../javascript/fetchWithTokenRefresh';
import { accessToken } from '../javascript/v2_authentication';

// Grace period before tearing down the subscription after the last subscriber leaves.
// Handles React StrictMode dev double-mount (cleanup → re-mount within the same tick)
// and quick navigations without thrashing the Supabase channel.
const TEARDOWN_GRACE_MS = 250;

// Matview lag before the count reflects a receipt UPDATE — same as V2's useInboxRealtime.
const MATVIEW_REFRESH_DELAY_MS = 250;

// A backgrounded or long-idle tab's in-flight fetch gets killed by the browser/OS (tab discard,
// sleep/wake, network handoff). The browser throws a native TypeError ("Failed to fetch" /
// "Load failed" / "NetworkError…") or an AbortError. These self-heal on the next poll, so they
// are noise rather than actionable errors. We match the abort name and the known network-failure
// messages — NOT every TypeError — so a real HTTP failure or a programming bug still reports.
const isTransientFetchError = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError') return true;
  const message = typeof error.message === 'string' ? error.message : '';
  return /failed to fetch|load failed|network ?error|aborterror|the (operation|user) (was )?aborted/i.test(message);
};

// The navbar number follows the user's primary tab: unread conversations in Focused for media,
// the whole inbox for promoters. Unread pitches never count here (product call, 2026-09-10).
let currentCount = 0;
// Stable integer profile id — the receiver_profile_id used as the count/realtime key.
// Preferred over the slug, which is a mutable friendly id (changes on profile rename).
let currentProfileId = null;
// null until resolved; only a known media type narrows the count to Focused.
let currentProfileType = null;
let unsubscribeInboxUnread = null;
let realtimeChannel = null;
let lastCommitTs = null;
let teardownTimer = null;
const subscribers = new Set();

const notifyAll = () => {
  subscribers.forEach((cb) => cb(currentCount));
};

const isMediaProfile = () => currentProfileType === 'type_media';

const fetchAndUpdate = async () => {
  if (!currentProfileId) {
    return;
  }
  try {
    // supabase-js captures access_token at init and never re-reads it, so long-idle tabs silently
    // fail with PGRST303. fetchWithTokenRefresh re-reads cookies per call and auto-refreshes on 401.
    const base = window.Qwoted.SUPABASE_API_EXTERNAL_URL;
    const params = new URLSearchParams({
      select: 'conversation_id',
      receiver_profile_id: `eq.${currentProfileId}`,
      receipt_read_at: 'is.null',
      receipt_archived_at: 'is.null',
      ...(isMediaProfile() ? { conversation_mailbox: 'eq.focused' } : {}),
    });
    const response = await fetchWithTokenRefresh(`${base}/rest/v1/conversations?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    currentCount = Array.isArray(data) ? data.length : 0;
    notifyAll();
  } catch (error) {
    // Transient transport aborts self-heal on the next poll — log at debug, don't page Rollbar.
    // Real HTTP failures (the HTTP <status> throw above) and any other error still report.
    if (isTransientFetchError(error)) {
      vlog.debug('Inbox unread count fetch aborted (transient)', error);
      return;
    }
    vlog.error('Failed to fetch inbox unread count', error);
    if (window.Rollbar) {
      window.Rollbar.error('Failed to fetch inbox unread count', error);
    }
  }
};

// Mirrors withV2ProfileSlug.jsx: V1 user ids are stored as user_migrate_id in V2's user_profiles.
// Resolves the stable integer profile id (the receiver_profile_id query/realtime key); the slug
// is a mutable friendly id and must not be used to identify the receiver.
const resolveProfile = async (userId) => {
  if (currentProfileId) {
    return currentProfileId;
  }
  // Fast path — V2 inbox may have already populated these on the page.
  if (window.Qwoted && window.Qwoted.CURRENT_PROFILE_ID) {
    currentProfileId = window.Qwoted.CURRENT_PROFILE_ID;
    currentProfileType = window.Qwoted.CURRENT_PROFILE_TYPE || currentProfileType;
    return currentProfileId;
  }
  if (!userId) {
    return null;
  }
  try {
    if (window.initializeSupabaseClient) {
      await window.initializeSupabaseClient();
    }
    const { data, error } = await window.Qwoted.supabaseClient
      .from('user_profiles')
      .select('profile_id, slug, type_of_profile')
      .eq('user_migrate_id', userId)
      .single();
    if (error || !data || !data.profile_id) {
      vlog.warn('V2 profile resolution failed for inbox badge', { userId, error });
      return null;
    }
    window.Qwoted.CURRENT_PROFILE_ID = data.profile_id;
    window.Qwoted.CURRENT_PROFILE_TYPE = data.type_of_profile;
    // Side effect for other consumers (e.g. the V2 inbox fast path) — not used as a key here.
    window.Qwoted.CURRENT_SLUG = data.slug;
    currentProfileId = data.profile_id;
    currentProfileType = data.type_of_profile || null;
    return currentProfileId;
  } catch (err) {
    vlog.warn('V2 profile resolution failed for inbox badge', err);
    return null;
  }
};

const openRealtimeChannel = () => {
  if (realtimeChannel || !currentProfileId || !window.supabaseClient) {
    return;
  }
  const channel = window.supabaseClient.channel(`inbox-badge-${currentProfileId}`);
  channel.on(
    'postgres_changes',
    {
      event: '*',
      schema: window.Qwoted.POSTGRES_SCHEMA,
      table: 'interact_receipts',
      filter: `receiver_profile_id=eq.${currentProfileId}`,
    },
    (payload) => {
      // Supabase occasionally redelivers the same event — dedupe by commit_timestamp.
      if (payload.commit_timestamp === lastCommitTs) {
        return;
      }
      lastCommitTs = payload.commit_timestamp;
      setTimeout(fetchAndUpdate, MATVIEW_REFRESH_DELAY_MS);
    },
  );
  channel.subscribe();
  realtimeChannel = channel;
};

const closeRealtimeChannel = () => {
  if (realtimeChannel) {
    realtimeChannel.unsubscribe();
    if (window.supabaseClient) {
      window.supabaseClient.removeChannel(realtimeChannel);
    }
    realtimeChannel = null;
  }
};

const onVisibilityChange = async () => {
  if (document.visibilityState !== 'visible') {
    return;
  }
  // Re-arm realtime auth so the badge resumes live updates after the JWT expired on an idle tab.
  if (!accessToken()) {
    await refreshAccessToken();
  }
  const token = accessToken();
  if (token && window.supabaseClient) {
    window.supabaseClient.realtime.setAuth(token);
  }
  // setAuth doesn't rejoin a channel that died while hidden — reopen it so live updates resume.
  if (realtimeChannel && realtimeChannel.state !== 'joined') {
    closeRealtimeChannel();
    openRealtimeChannel();
  }
  fetchAndUpdate();
};

// The inbox marks things read in-page and announces its counts: take the one this navbar shows.
const onInboxUnreadChanged = (counts) => {
  currentCount = isMediaProfile() ? counts.focused : counts.everything;
  notifyAll();
};

const startSubscription = async (userId) => {
  await resolveProfile(userId);
  openRealtimeChannel();
  fetchAndUpdate();
  document.addEventListener('visibilitychange', onVisibilityChange);
  unsubscribeInboxUnread = qapi.conversationsMailbox.onInboxUnreadChanged(onInboxUnreadChanged);
};

const stopSubscription = () => {
  closeRealtimeChannel();
  document.removeEventListener('visibilitychange', onVisibilityChange);
  if (unsubscribeInboxUnread) {
    unsubscribeInboxUnread();
    unsubscribeInboxUnread = null;
  }
  lastCommitTs = null;
  currentCount = 0;
  // Keep currentProfileId cached — same user, same page, no need to re-resolve on re-mount.
  notifyAll();
};

// Subscribe to the shared inbox unread count. Returns an unsubscribe function.
// First subscriber boots the work; last unsubscriber tears it down (with a grace period
// to avoid thrashing on React StrictMode double-mount or quick re-mounts).
const subscribeInboxUnreadCount = (userId, onChange) => {
  if (teardownTimer) {
    clearTimeout(teardownTimer);
    teardownTimer = null;
  }

  subscribers.add(onChange);
  // Surface the latest known value immediately so a re-mount sees the right number
  // without waiting for the next event.
  onChange(currentCount);

  if (subscribers.size === 1 && !realtimeChannel) {
    startSubscription(userId);
  }

  return () => {
    subscribers.delete(onChange);
    if (subscribers.size === 0) {
      teardownTimer = setTimeout(() => {
        teardownTimer = null;
        if (subscribers.size === 0) {
          stopSubscription();
        }
      }, TEARDOWN_GRACE_MS);
    }
  };
};

export default subscribeInboxUnreadCount;
